/**
 * The caregiver status machine (§17.2, §17.3).
 *
 * APPLICANT → ONBOARDING → ACTIVE; ACTIVE ⇄ INACTIVE; any → TERMINATED;
 * TERMINATED → DO_NOT_RETURN.
 *
 * The rule with teeth is §17.3's: **ACTIVE is unreachable while any
 * `blocks_scheduling` checklist item is outstanding**, and the refusal names
 * which. A refusal that says only "onboarding incomplete" sends somebody
 * hunting through a checklist; naming the two items that are missing is the
 * difference between a five-second fix and an email to HR.
 */
export const CAREGIVER_STATUSES = [
    'APPLICANT',
    'ONBOARDING',
    'ACTIVE',
    'INACTIVE',
    'TERMINATED',
    'DO_NOT_RETURN',
];
/**
 * Legal moves, written out rather than derived.
 *
 * Two decisions worth stating, because "any → TERMINATED" reads as though it
 * has none:
 *
 *   - **DO_NOT_RETURN does not go back to TERMINATED.** It is reachable only
 *     from TERMINATED and it goes nowhere. It records a decision that this
 *     person must not be re-hired, and a route back would let that decision be
 *     erased by a status change rather than reversed by whoever made it.
 *   - **APPLICANT does not go straight to ACTIVE.** Skipping ONBOARDING would
 *     skip the checklist, which is the only place §17.3's blocking items are
 *     recorded — and the gate below would then have nothing to gate.
 */
export const CAREGIVER_STATUS_TRANSITIONS = {
    APPLICANT: ['ONBOARDING', 'TERMINATED'],
    ONBOARDING: ['ACTIVE', 'TERMINATED'],
    ACTIVE: ['INACTIVE', 'TERMINATED'],
    INACTIVE: ['ACTIVE', 'TERMINATED'],
    TERMINATED: ['DO_NOT_RETURN'],
    // Terminal, deliberately. See above.
    DO_NOT_RETURN: [],
};
export function canTransition(from, to) {
    return CAREGIVER_STATUS_TRANSITIONS[from].includes(to);
}
const LABEL = {
    APPLICANT: 'an applicant',
    ONBOARDING: 'onboarding',
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    TERMINATED: 'terminated',
    DO_NOT_RETURN: 'marked do not return',
};
const label = (status) => LABEL[status];
/**
 * The blocking checklist items that are still outstanding.
 *
 * Exported because the console needs the same list to render the reason
 * somebody cannot be activated *before* they press the button, and computing it
 * twice is how the two answers drift.
 */
export function outstandingBlockingItems(checklist) {
    return checklist.filter((item) => item.blocksScheduling && !item.completed);
}
function explain(from, to) {
    if (from === 'DO_NOT_RETURN') {
        return ('This person is marked do not return, and that does not come off with a status ' +
            'change. If it was recorded in error, the person who recorded it reverses it.');
    }
    if (from === 'APPLICANT' && to === 'ACTIVE') {
        return ('An applicant goes through onboarding first. That is where the checklist lives, ' +
            'and the checklist is what says they can be sent out alone.');
    }
    // Every remaining `from` has at least one exit: DO_NOT_RETURN is the only
    // status with none, and it returned above. So there is no empty-list branch
    // here to write — one would be unreachable, and an unreachable branch is a
    // claim about the machine that nothing checks.
    const next = CAREGIVER_STATUS_TRANSITIONS[from];
    return (`Somebody ${label(from)} cannot become ${label(to)}. ` +
        `From here they can only become ${next.map(label).join(' or ')}.`);
}
/**
 * A result rather than an exception.
 *
 * An illegal transition is something a person did, usually with two tabs open.
 * It belongs in the response as a `userMessage`, not in a stack trace, and the
 * messages below are written for that person.
 */
export function caregiverStatusTransitions(request) {
    const { from, to, reason } = request;
    if (reason.trim() === '') {
        return {
            ok: false,
            error: {
                code: 'REASON_REQUIRED',
                message: 'Say why this is changing. It goes on the record with your name.',
            },
        };
    }
    if (from === to) {
        return {
            ok: false,
            error: { code: 'SAME_STATUS', message: `This person is already ${label(from)}.` },
        };
    }
    if (!canTransition(from, to)) {
        return { ok: false, error: { code: 'ILLEGAL_TRANSITION', message: explain(from, to) } };
    }
    if (to === 'ACTIVE') {
        const outstanding = outstandingBlockingItems(request.checklist ?? []);
        if (outstanding.length > 0) {
            return {
                ok: false,
                error: {
                    code: 'ONBOARDING_INCOMPLETE',
                    // Named, not counted. "Two items outstanding" is a message that sends
                    // somebody looking; the names are the fix.
                    message: 'This person cannot go active yet. Still outstanding: ' +
                        `${outstanding.map((item) => item.label).join(', ')}.`,
                    outstanding,
                },
            };
        }
    }
    return { ok: true, to, reason: reason.trim() };
}
//# sourceMappingURL=status.js.map